import { Icon } from '../c-mp/comp/Icon'
import { defineComponent } from '../c-mp/fun/defineComponent'
import { json } from '../c-mp/fun/json'
import { url } from '../c-mp/fun/url'
import { allEffectsDone, useEffect } from '../c-mp/fun/useEffect'
import { mutateState, useState } from '../c-mp/fun/useState'
import { average } from '../fun/average'
import { getDataOrThrow } from '../fun/getDataOrThrow'
import { getPersonIdFromUrl } from '../fun/getPersonIdFromUrl'
import { getPersonName } from '../fun/getPersonName'
import { makeSvgTree, makeTestSvg } from '../fun/makeSvgTree'
import { setPageTitle } from '../fun/setPageTitle'
import {
	getHistoryState,
	pushState,
	updateHistoryState,
} from '../fun/useRouting'
import { _ } from '../model/ccc'

export interface ITouch {
	screenX: number
	screenY: number
}

export interface IPoint {
	left: number
	top: number
}

export const PagePersonTreeComp = defineComponent<{}>(
	'PagePersonTreeComp',
	(props, $) => {
		const data = getDataOrThrow()

		useEffect('page title [ta77n8]', () => {
			setPageTitle(
				getPersonName(data.personsById[getPersonIdFromUrl() ?? '']) +
					' családfája',
			)
		})

		const state = useState('state', {
			scaleBase: 0,
			width: 100,
			height: 100,
			scrollLeft: 0,
			scrollTop: 0,
		})

		function getScale() {
			return 2 ** state.scaleBase
		}

		let frameRef: HTMLDivElement
		let testRef: HTMLDivElement

		useEffect('svg [t9h3mr]', () => {
			const personId = getPersonIdFromUrl()
			if (!personId || !(personId in data.personsById)) {
				return pushState(url`/person/${personId ?? ''}`)
			}
			const historyState = getHistoryState()
			;(async () => {
				testRef.innerHTML = makeTestSvg()
				const { svg, width, height } = await makeSvgTree(
					testRef.firstElementChild as SVGElement,
					{ data },
					personId,
				)
				frameRef.innerHTML = svg

				mutateState($.debugName, 'set size [t9j9ui]', () => {
					state.width = width
					state.height = height
					state.scaleBase = historyState.treeScaleBase ?? 0
				})
				await allEffectsDone()

				if (historyState.treeScrollLeft != null) {
					mutateState($.debugName, 'set scroll [tat1tr]', () => {
						state.scrollLeft = historyState.treeScrollLeft ?? 0
						state.scrollTop = historyState.treeScrollTop ?? 0
					})
				} else {
					scrollHome()
				}
			})()
		})

		async function changeScale(value: number, x?: number, y?: number) {
			const size = frameRef.getBoundingClientRect()
			const scrollTop = size.top + state.scrollTop
			const scrollLeft = size.left + state.scrollLeft
			const oldScale = getScale()
			const centerX = (scrollLeft + (x ?? size.width / 2)) / oldScale
			const centerY = (scrollTop + (y ?? size.height / 2)) / oldScale
			mutateState($.debugName, 'set scaleBase [t9j9kf]', () => {
				state.scaleBase = value
			})
			await allEffectsDone()
			const newScale = getScale()
			mutateState($.debugName, 'set scroll [tat1v2]', () => {
				state.scrollLeft = centerX * newScale - (x ?? size.width / 2)
				state.scrollTop = centerY * newScale - (y ?? size.height / 2)
			})
			await allEffectsDone()
			updateHistoryState((it) => ({
				...it,
				treeScaleBase: value,
				treeScrollLeft: frameRef.scrollLeft,
				treeScrollTop: frameRef.scrollTop,
			}))
		}

		async function scrollHome() {
			const personId = getPersonIdFromUrl()
			if (!personId) throw new Error(`[t9vthr] Person ID missing.`)
			const svgElem = frameRef.firstElementChild as SVGElement
			const elem = svgElem.querySelector(
				`[id="${personId}"]`,
			) as SVGAElement | null
			if (!elem) {
				console.error(json`[t9jace] Missing person elem: ${personId}`)
				return
			}

			mutateState($.debugName, 'reset scaleBase and scroll [t9jcz4]', () => {
				state.scaleBase = 0
				state.scrollTop = 0
				state.scrollLeft = 0
			})
			await allEffectsDone()

			const size = elem.getBoundingClientRect()
			const frameSize = frameRef.getBoundingClientRect()
			const scrollTop =
				(size.y + size.height / 2) * getScale() - frameSize.height / 2
			const scrollLeft =
				(size.x + size.width / 2) * getScale() - frameSize.width / 2
			mutateState($.debugName, 'set scroll [tat1xe]', () => {
				state.scrollTop = scrollTop
				state.scrollLeft = scrollLeft
			})

			clearHistoryState()
		}

		let draggedFromScroll: IPoint = { top: 0, left: 0 }
		let draggedFromScreen: IPoint = { top: 0, left: 0 }
		let isDragSignificant = false
		let isNavigationPrevented = false
		let pointersDown = new Map<number, ITouch>()
		let scaleInitial = 0
		let pinchDistanceInitial = 0

		function getPinchCenterPoint(): IPoint {
			return {
				left: average(
					Array.from(pointersDown.values()).map((it) => it.screenX),
				),
				top: average(Array.from(pointersDown.values()).map((it) => it.screenY)),
			}
		}

		function getPinchDistance() {
			const [one, two] = pointersDown.values().toArray() as [ITouch, ITouch]
			return (
				Math.sqrt(
					(one.screenX - two.screenX) ** 2 + (one.screenY - two.screenY) ** 2,
				) || 1
			)
		}
		function onPointerDown(e: PointerEvent) {
			e.preventDefault()
			pointersDown.set(e.pointerId, { screenX: e.screenX, screenY: e.screenY })
			draggedFromScroll = {
				top: state.scrollTop,
				left: state.scrollLeft,
			}
			draggedFromScreen = getPinchCenterPoint()
			scaleInitial = getScale()
			if (pointersDown.size === 1) {
				isDragSignificant = false
				window.addEventListener('pointermove', onPointerMove, {
					passive: false,
				})
				window.addEventListener('pointercancel', onPointerUp, {
					passive: false,
				})
				window.addEventListener('pointerleave', onPointerUp, { passive: false })
				window.addEventListener('pointerup', onPointerUp, { passive: false })
			} else if (pointersDown.size >= 2) {
				pinchDistanceInitial = getPinchDistance()
				isDragSignificant = true
			}
		}
		async function onPointerMove(e: PointerEvent) {
			e.preventDefault()
			const pointerDown = pointersDown.get(e.pointerId)
			if (!pointerDown) throw new Error(`[tatgk6]`)
			pointerDown.screenX = e.screenX
			pointerDown.screenY = e.screenY
			if (pointersDown.size >= 2) {
				const point = getPinchCenterPoint()
				const pinchDistance = getPinchDistance()
				const scale = (pinchDistance / pinchDistanceInitial) * scaleInitial
				await changeScale(Math.log2(scale), point.left, point.top)
				mutateState($.debugName, 'set scroll [tatkgo]', () => {
					state.scrollTop -= point.top - draggedFromScreen.top
					state.scrollLeft -= point.left - draggedFromScreen.left
				})
				await allEffectsDone()
				draggedFromScreen = point
			} else {
				const point = getPinchCenterPoint()
				mutateState($.debugName, 'set scroll [tat1zg]', () => {
					state.scrollTop =
						draggedFromScroll.top - (point.top - draggedFromScreen.top)
					state.scrollLeft =
						draggedFromScroll.left - (point.left - draggedFromScreen.left)
				})
				if (
					Math.abs(point.top - draggedFromScreen.top) > 10 ||
					Math.abs(point.left - draggedFromScreen.left) > 10
				) {
					isDragSignificant = true
				}
			}
		}
		function onPointerUp(e?: PointerEvent) {
			if (e) {
				e.preventDefault()
				pointersDown.delete(e.pointerId)
				if (pointersDown.size > 0) {
					draggedFromScroll = {
						top: state.scrollTop,
						left: state.scrollLeft,
					}
					draggedFromScreen = getPinchCenterPoint()
					scaleInitial = getScale()
					if (pointersDown.size >= 2) {
						pinchDistanceInitial = getPinchDistance()
					}
				}
			} else {
				for (const key of Array.from(pointersDown.keys())) {
					pointersDown.delete(key)
				}
			}
			if (pointersDown.size === 0) {
				window.removeEventListener('pointermove', onPointerMove)
				window.removeEventListener('pointercancel', onPointerUp)
				window.removeEventListener('pointerleave', onPointerUp)
				window.removeEventListener('pointerup', onPointerUp)
				saveHistoryState()
				if (isDragSignificant) {
					console.log(`[ta1en8] drag significant`)
					isNavigationPrevented = true
				} else {
					console.log(`[ta1enm] drag NOT significant`)
				}
			}
		}
		function onClick(e: MouseEvent) {
			if (isNavigationPrevented) {
				isNavigationPrevented = false
				e.preventDefault()
				e.stopPropagation()
			}
		}

		useEffect('mouse handlers [ta1ad2]', () => {
			return onPointerUp
		})

		function onWheel(e: WheelEvent) {
			e.preventDefault()
			const size = frameRef.getBoundingClientRect()
			changeScale(
				Math.round(state.scaleBase) + (e.deltaY < 0 ? 1 : -1),
				e.clientX - size.x,
				e.clientY - size.y,
			)
		}

		function onScroll() {
			saveHistoryState()
		}

		function clearHistoryState() {
			updateHistoryState((it) => {
				delete it.treeScaleBase
				delete it.treeScrollLeft
				delete it.treeScrollTop
				return it
			})
		}
		function saveHistoryState() {
			updateHistoryState((it) => ({
				...it,
				treeScaleBase: state.scaleBase,
				treeScrollLeft: state.scrollLeft,
				treeScrollTop: state.scrollTop,
			}))
		}

		return (
			<>
				<div class='ccc_tree_test' ref={(it) => (testRef = it)}></div>
				<div
					class='ccc_tree'
					style={() => ({
						'--scale-tat0mm': getScale() + '',
						'--scroll-left-tat1pz': state.scrollLeft + 'px',
						'--scroll-top-tat1qe': state.scrollTop + 'px',
					})}
					ref={(it) => (frameRef = it)}
					onpointerdown={onPointerDown}
					onwheel={onWheel}
					onclick={onClick}
					onscroll={onScroll}
				></div>
				<div class='ccc_tree_tools_start'>
					<button
						class={_.ccc_button}
						title='Ugrás a kezdőpontra'
						onclick={async () => {
							scrollHome()
						}}
					>
						<Icon
							svg={`<svg class="${_.ccc_icon__large}" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 16 16"><path fill="currentColor" d="M8.5.5a.5.5 0 0 0-1 0v.518A7 7 0 0 0 1.018 7.5H.5a.5.5 0 0 0 0 1h.518A7 7 0 0 0 7.5 14.982v.518a.5.5 0 0 0 1 0v-.518A7 7 0 0 0 14.982 8.5h.518a.5.5 0 0 0 0-1h-.518A7 7 0 0 0 8.5 1.018zm-6.48 7A6 6 0 0 1 7.5 2.02v.48a.5.5 0 0 0 1 0v-.48a6 6 0 0 1 5.48 5.48h-.48a.5.5 0 0 0 0 1h.48a6 6 0 0 1-5.48 5.48v-.48a.5.5 0 0 0-1 0v.48A6 6 0 0 1 2.02 8.5h.48a.5.5 0 0 0 0-1zM8 10a2 2 0 1 0 0-4a2 2 0 0 0 0 4"/></svg>`}
						/>
					</button>
				</div>
				<div class='ccc_tree_tools_center'>
					<button
						class={_.ccc_button}
						onclick={async () => {
							const personId = getPersonIdFromUrl()
							if (!personId) throw new Error(`[t9vtgl] Person ID missing.`)

							testRef.innerHTML = makeTestSvg()
							const { svg } = await makeSvgTree(
								testRef.firstElementChild as SVGElement,
								{ data, forDownload: true },
								personId,
							)
							const dataUri = URL.createObjectURL(
								new Blob([svg], {
									type: 'image/svg+xml',
								}),
							)
							const downloadLink = (
								<a href={dataUri} download={`${personId}.svg`}></a>
							) as HTMLAnchorElement
							document.body.append(downloadLink)
							downloadLink.click()
							downloadLink.remove()
						}}
					>
						<Icon
							svg={`<svg class="${_.ccc_icon__large}" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-download" viewBox="0 0 16 16">
  <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5"/>
  <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z"/>
</svg>`}
						/>{' '}
						letöltés
					</button>
				</div>
				<div class='ccc_tree_tools_end'>
					<button
						class={_.ccc_button}
						title='Nagyítás'
						onclick={() => {
							changeScale(state.scaleBase + 1)
						}}
					>
						<Icon
							svg={`<svg class="${_.ccc_icon__large}" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 16 16"><g fill="currentColor"><path fill-rule="evenodd" d="M6.5 12a5.5 5.5 0 1 0 0-11a5.5 5.5 0 0 0 0 11M13 6.5a6.5 6.5 0 1 1-13 0a6.5 6.5 0 0 1 13 0"/><path d="M10.344 11.742q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1a6.5 6.5 0 0 1-1.398 1.4z"/><path fill-rule="evenodd" d="M6.5 3a.5.5 0 0 1 .5.5V6h2.5a.5.5 0 0 1 0 1H7v2.5a.5.5 0 0 1-1 0V7H3.5a.5.5 0 0 1 0-1H6V3.5a.5.5 0 0 1 .5-.5"/></g></svg>`}
						/>
					</button>
					<button
						class={_.ccc_button}
						title='Kicsinyítés'
						onclick={async () => {
							changeScale(state.scaleBase - 1)
						}}
					>
						<Icon
							svg={`<svg class="${_.ccc_icon__large}" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 16 16"><g fill="currentColor"><path fill-rule="evenodd" d="M6.5 12a5.5 5.5 0 1 0 0-11a5.5 5.5 0 0 0 0 11M13 6.5a6.5 6.5 0 1 1-13 0a6.5 6.5 0 0 1 13 0"/><path d="M10.344 11.742q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1a6.5 6.5 0 0 1-1.398 1.4z"/><path fill-rule="evenodd" d="M3 6.5a.5.5 0 0 1 .5-.5h6a.5.5 0 0 1 0 1h-6a.5.5 0 0 1-.5-.5"/></g></svg>`}
						/>
					</button>
				</div>
			</>
		)
	},
)
